import React, { useEffect, useMemo, useRef, useState } from "react";
import { API_BASE, jsonHeaders, authHeaders } from "../../utils/api";
import "../../styles/admin-common.css";
import "./AdmissionFAQs.css";

const AdmissionFAQs = () => {
  const [faqTypes, setFaqTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [newTypeName, setNewTypeName] = useState("");
  const [faqs, setFaqs] = useState([]);
  const [faqLogs, setFaqLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [form, setForm] = useState({ id: null, question: "", answer: "" });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const editorRef = useRef(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [showTableModal, setShowTableModal] = useState(false);
  const [linkValue, setLinkValue] = useState({ text: '', url: '', type: 'url' });
  const [tableSize, setTableSize] = useState({ rows: 2, cols: 2 });
  const savedRange = useRef(null);

  const role = useMemo(() => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.role || null;
    } catch {
      return null;
    }
  }, []);

  const isSuperadmin = role === "superadmin";

  const fetchFaqTypes = async () => {
    setError("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/faqs/types`, {
        headers: jsonHeaders(),
      });
      if (!res.ok) throw new Error("Failed to load FAQ types");
      const data = await res.json();
      setFaqTypes(data);

      if (!selectedTypeId && data.length > 0) {
        setSelectedTypeId(String(data[0].id));
      }
    } catch (err) {
      setError(err.message || "Error loading FAQ types");
    }
  };

  useEffect(() => {
    fetchFaqTypes();
  }, []);

  useEffect(() => {
    const fetchFaqs = async () => {
      if (!selectedTypeId) {
        setFaqs([]);
        return;
      }
      setLoading(true);
      setError("");
      try {
        let url = `${API_BASE}/api/admin/faqs?typeId=${encodeURIComponent(selectedTypeId)}`;
        if (statusFilter !== "all") {
          url += `&status=${encodeURIComponent(statusFilter)}`;
        }
        const res = await fetch(url, { headers: jsonHeaders() });
        if (!res.ok) throw new Error("Failed to load FAQs");
        const data = await res.json();
        setFaqs(data);
      } catch (err) {
        setError(err.message || "Error loading FAQs");
      } finally {
        setLoading(false);
      }
    };
    fetchFaqs();
  }, [selectedTypeId, statusFilter]);

  const handleTypeChange = (e) => {
    setSelectedTypeId(e.target.value);
    setForm({ id: null, question: "", answer: "" });
  };

  const createType = async () => {
    if (!newTypeName.trim()) return;
    setError("");
    setSuccess("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/faqs/types`, {
        method: "POST",
        headers: jsonHeaders(),
        body: JSON.stringify({ name: newTypeName }),
      });
      if (!res.ok) throw new Error("Failed to create FAQ type");

      setNewTypeName("");
      await fetchFaqTypes();
      setSuccess("FAQ type created");
    } catch (err) {
      setError(err.message || "Error creating FAQ type");
    }
  };



  const handleFaqFormChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAnswerHtmlChange = (html) => {
    setForm((prev) => ({ ...prev, answer: html }));
  };

  const applyEditorCommand = (cmd, value = null) => {
    if (!editorRef.current) return;
    editorRef.current.focus();
    document.execCommand(cmd, false, value);
    handleAnswerHtmlChange(editorRef.current.innerHTML);
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) savedRange.current = sel.getRangeAt(0).cloneRange();
  };

  const restoreSelection = () => {
    if (!savedRange.current) return;
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(savedRange.current);
  };

  const insertLink = () => {
    restoreSelection();
    editorRef.current.focus();
    const { text, url, type } = linkValue;
    const href = type === 'phone' ? `tel:${url}` : type === 'email' ? `mailto:${url}` : url;
    const sel = window.getSelection();
    const selectedText = sel && sel.toString() ? sel.toString() : text;
    document.execCommand('insertHTML', false, `<a href="${href}" target="${type === 'url' ? '_blank' : '_self'}">${selectedText}</a>`);
    handleAnswerHtmlChange(editorRef.current.innerHTML);
    setShowLinkModal(false);
    setLinkValue({ text: '', url: '', type: 'url' });
  };

  const insertTable = () => {
    restoreSelection();
    editorRef.current.focus();
    const { rows, cols } = tableSize;
    let html = '<table border="1" style="border-collapse:collapse;width:100%;margin:8px 0"><thead><tr>';
    for (let c = 0; c < cols; c++) html += `<th style="padding:6px 10px;background:#f3f4f6">Header ${c + 1}</th>`;
    html += '</tr></thead><tbody>';
    for (let r = 0; r < rows - 1; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) html += '<td style="padding:6px 10px">Cell</td>';
      html += '</tr>';
    }
    html += '</tbody></table><br/>';
    document.execCommand('insertHTML', false, html);
    handleAnswerHtmlChange(editorRef.current.innerHTML);
    setShowTableModal(false);
    setTableSize({ rows: 2, cols: 2 });
  };

  const resetForm = () => {
    setForm({ id: null, question: '', answer: '' });
    setFaqLogs([]);
    if (editorRef.current) editorRef.current.innerHTML = '';
  };

  const saveFaq = async () => {
    if (!selectedTypeId) return setError("Please select FAQ type");
    if (!form.question.trim() || !form.answer.trim())
      return setError("Question and answer are required");

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        faqTypeId: selectedTypeId,
        question: form.question,
        answer: form.answer,
      };

      const isEdit = !!form.id;
      const url = isEdit
        ? `${API_BASE}/api/admin/faqs/${form.id}`
        : `${API_BASE}/api/admin/faqs`;
      const method = isEdit ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: jsonHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) throw new Error("Failed to save FAQ");

      // Refetch FAQs
      const refetchUrl = `${API_BASE}/api/admin/faqs?typeId=${encodeURIComponent(selectedTypeId)}${
        statusFilter !== "all" ? `&status=${encodeURIComponent(statusFilter)}` : ""
      }`;
      const refetchRes = await fetch(refetchUrl, { headers: jsonHeaders() });
      if (refetchRes.ok) {
        const data = await refetchRes.json();
        setFaqs(data);
      }
      resetForm();
      setSuccess("FAQ saved successfully");
    } catch (err) {
      setError(err.message || "Error saving FAQ");
    } finally {
      setSaving(false);
    }
  };

  const fetchLogs = async (id) => {
    setLogsLoading(true);
    setFaqLogs([]);

    try {
      const res = await fetch(`${API_BASE}/api/admin/faqs/${id}/logs`, {
        headers: jsonHeaders(),
      });
      if (!res.ok) throw new Error("Failed to load logs");

      const data = await res.json();
      setFaqLogs(data);
    } catch {
      setFaqLogs([]);
    } finally {
      setLogsLoading(false);
    }
  };

  const onEditFaq = (faq) => {
    setForm({ id: faq.id, question: faq.question, answer: faq.answer });
    fetchLogs(faq.id);
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = faq.answer || '';
    }, 0);
  };

  const deleteFaq = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    setError("");
    setSuccess("");

    try {
      const res = await fetch(`${API_BASE}/api/admin/faqs/${id}`, {
        method: "DELETE",
        headers: jsonHeaders(),
      });

      if (!res.ok) throw new Error("Failed to delete FAQ");

      // Refetch FAQs
      const refetchUrl = `${API_BASE}/api/admin/faqs?typeId=${encodeURIComponent(selectedTypeId)}${
        statusFilter !== "all" ? `&status=${encodeURIComponent(statusFilter)}` : ""
      }`;
      const refetchRes = await fetch(refetchUrl, { headers: jsonHeaders() });
      if (refetchRes.ok) {
        const data = await refetchRes.json();
        setFaqs(data);
      }
      setSuccess("FAQ deleted");
    } catch (err) {
      setError(err.message || "Error deleting FAQ");
    }
  };

  const postAction = async (id, action, body) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/faqs/${id}/${action}`, {
        method: "POST",
        headers: jsonHeaders(),
        body: body ? JSON.stringify(body) : undefined,
      });

      if (!res.ok) throw new Error(`Failed to ${action}`);
      
      const result = await res.json();
      
      // Force update the FAQ status to 'live' for approve action
      const newStatus = action === 'approve' ? 'live' : result.status;
      
      // Update the specific FAQ in the list
      setFaqs(prevFaqs => 
        prevFaqs.map(faq => 
          faq.id === id ? { ...faq, status: newStatus } : faq
        )
      );
      
      setSuccess(action === 'approve' ? 'FAQ approved and made live' : `Action ${action} completed`);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="admin-page">
      <div className="page-heading">
        <h1>Admission FAQs</h1>
        <p>Manage FAQs by type. Coordinator edits, Superadmin approves.</p>
      </div>

      {error && <div className="admin-alert error">{error}</div>}
      {success && <div className="admin-alert success">{success}</div>}

      <div className="admin-actions">
        <button className="btn-secondary" onClick={async () => {
          if (!selectedTypeId) { setError('Please select a FAQ type first'); return; }
          try {
            const res = await fetch(`${API_BASE}/api/admin/faqs/export?typeId=${selectedTypeId}`, { headers: authHeaders() });
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a'); a.href = url; a.download = `faqs_type_${selectedTypeId}.csv`; a.click(); URL.revokeObjectURL(url);
          } catch { setError('Export failed'); }
        }}>📥 Export CSV</button>
      </div>

      <div className="admin-grid">
        <div className="card">
          <h2>{form.id ? "Edit FAQ" : "Create FAQ"}</h2>
          
          <form className="admin-form" onSubmit={(e) => { e.preventDefault(); saveFaq(); }}>
            <label>
              FAQ Type
              <select value={selectedTypeId} onChange={handleTypeChange}>
                <option value="">-- Select Type --</option>
                {faqTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </label>

            <label>
              New FAQ Type
              <div style={{display: 'flex', gap: '10px'}}>
                <input
                  type="text"
                  placeholder="New FAQ type"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                />
                <button type="button" className="btn-secondary" onClick={createType}>Add Type</button>
              </div>
            </label>



            <label>
              Question *
              <input
                type="text"
                name="question"
                value={form.question}
                onChange={handleFaqFormChange}
                required
              />
            </label>

            <label>
              Answer *
              {/* Toolbar */}
              <div className="rte-toolbar">
                <button type="button" title="Bold" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('bold'); }}><b>B</b></button>
                <button type="button" title="Italic" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('italic'); }}><i>I</i></button>
                <button type="button" title="Underline" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('underline'); }}><u>U</u></button>
                <span className="rte-sep"/>
                <button type="button" title="Bullet List" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('insertUnorderedList'); }}>• List</button>
                <button type="button" title="Numbered List" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('insertOrderedList'); }}>1. List</button>
                <span className="rte-sep"/>
                <button type="button" title="Insert Table" onMouseDown={(e) => { e.preventDefault(); saveSelection(); setShowTableModal(true); }}>⊞ Table</button>
                <button type="button" title="Insert Link / Phone / Email" onMouseDown={(e) => { e.preventDefault(); saveSelection(); setShowLinkModal(true); }}>🔗 Link</button>
                <span className="rte-sep"/>
                <button type="button" title="Clear Formatting" onMouseDown={(e) => { e.preventDefault(); applyEditorCommand('removeFormat'); }}>✕ Clear</button>
              </div>
              {/* Editor */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className="rte-editor"
                onInput={() => handleAnswerHtmlChange(editorRef.current.innerHTML)}
              />
            </label>

            {/* Link / Phone / Email Modal */}
            {showLinkModal && (
              <div className="rte-modal-overlay">
                <div className="rte-modal">
                  <h3>Insert Link</h3>
                  <label>Type
                    <select value={linkValue.type} onChange={(e) => setLinkValue({ ...linkValue, type: e.target.value })}>
                      <option value="url">URL</option>
                      <option value="phone">Phone Number</option>
                      <option value="email">Email Address</option>
                    </select>
                  </label>
                  <label>Display Text
                    <input type="text" placeholder="Link text" value={linkValue.text} onChange={(e) => setLinkValue({ ...linkValue, text: e.target.value })} />
                  </label>
                  <label>
                    {linkValue.type === 'phone' ? 'Phone Number' : linkValue.type === 'email' ? 'Email Address' : 'URL'}
                    <input
                      type="text"
                      placeholder={linkValue.type === 'phone' ? '9876543210' : linkValue.type === 'email' ? 'example@email.com' : 'https://'}
                      value={linkValue.url}
                      onChange={(e) => setLinkValue({ ...linkValue, url: e.target.value })}
                    />
                  </label>
                  <div className="rte-modal-actions">
                    <button type="button" className="btn-primary" onClick={insertLink}>Insert</button>
                    <button type="button" className="btn-secondary" onClick={() => setShowLinkModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* Table Modal */}
            {showTableModal && (
              <div className="rte-modal-overlay">
                <div className="rte-modal">
                  <h3>Insert Table</h3>
                  <label>Rows
                    <input type="number" min="1" max="20" value={tableSize.rows} onChange={(e) => setTableSize({ ...tableSize, rows: parseInt(e.target.value) || 1 })} />
                  </label>
                  <label>Columns
                    <input type="number" min="1" max="10" value={tableSize.cols} onChange={(e) => setTableSize({ ...tableSize, cols: parseInt(e.target.value) || 1 })} />
                  </label>
                  <div className="rte-modal-actions">
                    <button type="button" className="btn-primary" onClick={insertTable}>Insert</button>
                    <button type="button" className="btn-secondary" onClick={() => setShowTableModal(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={saving}>
                {saving ? "Saving..." : "Save FAQ"}
              </button>
              {form.id && (
                <button type="button" className="btn-secondary" onClick={resetForm}>Cancel</button>
              )}
            </div>
          </form>
        </div>

        <div className="card">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px'}}>
            <h2>FAQs List</h2>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd'}}>
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="live">Live</option>
            </select>
          </div>
          {loading ? (
            <p>Loading...</p>
          ) : faqs.length === 0 ? (
            <p>No FAQs available.</p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, index) => (
                  <tr key={faq.id}>
                    <td>{index + 1}</td>
                    <td>{faq.question}</td>
                    <td><span className={`status-badge ${faq.status}`}>{faq.status === 'live' ? 'Approved' : faq.status === 'draft' ? 'Pending' : faq.status.charAt(0).toUpperCase() + faq.status.slice(1)}</span></td>
                    <td className="actions">
                      <button className="btn-edit" onClick={() => onEditFaq(faq)}>✏️ Edit</button>
                      <button className="btn-delete" onClick={() => deleteFaq(faq.id)}>🗑️ Delete</button>
                      {isSuperadmin && faq.status !== 'live' && <button className="btn-edit" onClick={() => postAction(faq.id, "approve")}>✅ Approve & Make Live</button>}
                      {isSuperadmin && faq.status !== 'rejected' && faq.status !== 'live' && <button className="btn-delete" onClick={() => postAction(faq.id, "reject", { reason: prompt("Reason for rejection?") || "" })}>❌ Reject</button>}

                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedTypeId && (
        <button
          className="export-btn"
          onClick={async () => {
            try {
              const res = await fetch(
                `${API_BASE}/api/admin/faqs/export?typeId=${selectedTypeId}`,
                { headers: authHeaders() }
              );
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);

              const a = document.createElement("a");
              a.href = url;
              a.download = `faqs_type_${selectedTypeId}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              setError("Export failed");
            }
          }}
        >
          Export FAQs
        </button>
      )}
    </div>
  );
};

export default AdmissionFAQs;
